import {
  isMobile,
  mobileVendor,
  mobileModel,
  deviceType,
  deviceDetect,
} from 'react-device-detect'
import * as Fingerprint2 from 'fingerprintjs2'

import {
  isInStandaloneMode,
  logError,
  getErrorMessage,
  isEmpty,
  isLocal,
  timeSinceTimestamp,
} from 'src/utils/helpers'
import { postRequest } from 'src/utils/request-client'
import Storage from 'src/utils/storage'


export default class EventsService {
  static isWidget = null

  /**
   * Set all received options as class props.
   *
   * @param options
   */
  static setProps(options = {}) {
    Object.keys(options).map((key) => {
      if(EventsService.hasOwnProperty(key)) {
        EventsService[key] = options[key]
      }
      return null
    });
  }

  /**
   * Log visit event in backend.
   *
   * @param {object} options
   * @param {string} reportEvent Type of event to register. Possible values:
   * <ul>
   *   <li>access</li>
   * </ul>
   * @param {string} reportTarget Type of event to register. Possible values:
   * <ul>
   *   <li>visit - on page load</li>
   *   <li>visitAsKioskUser - on page load when using "Login as Kiosk user" option</li>
   *   <li>logInPin - after logging in with PIN</li>
   *   <li>logInFromEmailLink - when using a link from automated or manual emails</li>
   *   <li>logInAsKioskUserLink - when admin uses "Login as Kiosk user" option</li>
   *   <li>logOut - after logging out</li>
   *   <li>logOutAuto - logging out when kiosk is idle for 15 minutes</li>
   *   <li>logOutAsKioskUser - after logging out but when using "Login as kiosk user" option</li>
   * </ul>
   */
  static async registerEvent(options, reportEvent, reportTarget) {

    // Do not register events in development
    if(isLocal()) {
      return false
    }

    const isVisit = reportTarget === 'visit' || reportTarget === 'visitAsKioskUser'

    // Visit events
    if(isVisit) {

      // Last time we registered a visit
      const lastVisitTimestamp = parseInt(Storage.getItem('lastRegisteredVisitTimestamp'))

      // Skip registering visits for 60 minutes
      const isNewVisit = !lastVisitTimestamp || timeSinceTimestamp(lastVisitTimestamp, 'minutes') >= 60
      if(!isNewVisit) {
        return false
      }
    }

    if(!isEmpty(options)) {
      EventsService.setProps(options)
    }

    const registerData = {
      event: reportEvent,
      target: reportTarget,
      ...await EventsService.getVisitorData(options),
    }

    return postRequest({
      base: '/events/register',
    }, registerData).then(() => {

      if(isVisit) {
        Storage.setItem('lastRegisteredVisitTimestamp', new Date().getTime())
      }

      return true
    }).catch((e) => {
      logError(getErrorMessage(e, 'Log Kiosk visit'), e)
      return false
    })
  }

  /**
   * Collects various data of current Kiosk visitor.
   *
   * @param {object} options
   * @return {object} Kiosk Client (widget/direct/app), userAgent, device, etc.
   */
  static async getVisitorData(options) {
    if(!isEmpty(options)) {
      EventsService.setProps(options)
    }

    // Get device data
    const device = deviceDetect()

    return {
      considerNotLoggedIn: options.hasOwnProperty('considerNotLoggedIn') && options.considerNotLoggedIn === true,
      fingerprintHash: await EventsService.getFingerprint(),
      kioskClient: EventsService.getKioskClient(),
      device: {
        isMobile,
        mobileVendor,
        mobileModel,
        deviceType,
        ...device,
      }
    }
  }

  /**
   * Checks and returns current Kiosk Client.
   *
   * Requires EventsService.isWidget to be set.
   *
   * @return {string} Possible values:
   * <ul>
   *   <li>`widget` - Through widget on a client's website</li>
   *   <li>`app` - When installed as standalone app (either mobile or desktop)</li>
   *   <li>`direct` - When directly opened in browser at {broker}.kioskassist.com</li>
   * </ul>
   */
  static getKioskClient() {
    // This is passed all the way from where everything is initialized
    if(EventsService.isWidget === true) {
      return 'widget'
    }
    // Just checks current global window, document, navigator, etc.
    if(isInStandaloneMode() === true) {
      return 'app'
    }
    // Defaults to direct access at {broker}.kioskassist.com
    return 'direct'
  }

  /**
   * Uses Fingerprintjs2 (https://github.com/fingerprintjs/fingerprintjs2)
   * to get unique ID/fingerprint of current visitor.
   *
   * @return {Promise<string>}
   */
  static getFingerprint() {
    return new Promise((resolve, reject) => {
      async function getFingerprintHash() {
        try {
          const components = await Fingerprint2.getPromise()
          const values = components.map(component => component.value)
          return String(Fingerprint2.x64hash128(values.join(''), 31))
        } catch (e) {
          reject(e)
        }
      }

      try {
        if (window && window.requestIdleCallback) {
          window.requestIdleCallback(async () => resolve(await getFingerprintHash()))
        } else {
          setTimeout(async () => resolve(await getFingerprintHash()), 500)
        }
      } catch (e) {
        setTimeout(async () => resolve(await getFingerprintHash()), 1000)
      }
    })
  }
}